import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ScanSearch, Plus, Trash2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function StepCheck({ assignment, reload }) {
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(assignment.parsed);
  const [req, setReq] = useState(() => assignment.confirmed_requirements || (assignment.parsed ? {
    word_limit: assignment.parsed.word_limit, referencing_style: assignment.parsed.referencing_style, deadline: assignment.parsed.deadline,
  } : { word_limit: "", referencing_style: "", deadline: "" }));
  const [rubric, setRubric] = useState(() => assignment.confirmed_rubric?.length ? assignment.confirmed_rubric : (assignment.parsed?.rubric || []));
  const [noRubric, setNoRubric] = useState(assignment.no_rubric || false);
  const [saving, setSaving] = useState(false);

  const runCheck = async () => {
    setParsing(true);
    try {
      const { data } = await api.post(`/assignments/${assignment.id}/check`);
      setParsed(data);
      setReq({ word_limit: data.word_limit, referencing_style: data.referencing_style, deadline: data.deadline });
      setRubric(data.rubric || []);
      toast.success("We parsed your brief — review below.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setParsing(false); }
  };

  const setRow = (i, field, val) => setRubric((r) => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  const addRow = () => setRubric((r) => [...r, { criterion: "", weight: "", description: "" }]);
  const delRow = (i) => setRubric((r) => r.filter((_, idx) => idx !== i));

  const confirm = async () => {
    setSaving(true);
    try {
      await api.patch(`/assignments/${assignment.id}/confirm`, {
        confirmed_requirements: req, confirmed_rubric: noRubric ? [] : rubric, no_rubric: noRubric,
      });
      toast.success("Details confirmed. You can generate a blueprint now.");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!assignment.brief_text) {
    return <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="check-no-brief">Upload an assignment brief first, then we'll check what we found.</div>;
  }

  if (!parsed) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-border bg-card p-10 text-center" data-testid="check-run">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><ScanSearch className="h-6 w-6" /></div>
        <h3 className="mt-4 font-display text-xl font-semibold">Check what we found</h3>
        <p className="mt-1 max-w-md text-muted-foreground">We'll read your brief and rubric and pull out the requirements and marking criteria. This step is free.</p>
        <Button onClick={runCheck} disabled={parsing} className="mt-6 bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="run-check-btn">
          {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanSearch className="mr-2 h-4 w-4" />}{parsing ? "Reading…" : "Check my brief"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-check">
      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-lg font-semibold">What we found</h3>
        <p className="mt-1 text-sm text-muted-foreground">{parsed.summary}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {[
            { k: "word_limit", label: "Word limit" },
            { k: "referencing_style", label: "Referencing style" },
            { k: "deadline", label: "Deadline" },
          ].map((f) => (
            <div key={f.k}>
              <Label className="text-xs text-muted-foreground">{f.label}</Label>
              <Input value={req[f.k] || ""} onChange={(e) => setReq({ ...req, [f.k]: e.target.value })} data-testid={`req-${f.k}`} className="mt-1" />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Rubric</h3>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={noRubric} onCheckedChange={setNoRubric} data-testid="no-rubric-toggle" /> No rubric mode
          </label>
        </div>
        {noRubric ? (
          <p className="mt-3 text-sm text-muted-foreground">We'll use generic marking criteria from the brief. Evaluation will be labelled "less accurate".</p>
        ) : (
          <>
            <div className="mt-4 space-y-2">
              <div className="hidden grid-cols-12 gap-2 px-1 text-xs font-medium uppercase text-muted-foreground sm:grid">
                <div className="col-span-4">Criterion</div><div className="col-span-2">Weight</div><div className="col-span-5">Expectations</div><div className="col-span-1" />
              </div>
              {rubric.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`rubric-row-${i}`}>
                  <Input className="col-span-12 sm:col-span-4" value={row.criterion} onChange={(e) => setRow(i, "criterion", e.target.value)} placeholder="Criterion" />
                  <Input className="col-span-5 sm:col-span-2" value={row.weight} onChange={(e) => setRow(i, "weight", e.target.value)} placeholder="20%" />
                  <Input className="col-span-6 sm:col-span-5" value={row.description || ""} onChange={(e) => setRow(i, "description", e.target.value)} placeholder="What the marker expects" />
                  <Button variant="ghost" size="icon" className="col-span-1" onClick={() => delRow(i)} data-testid={`rubric-del-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-3" onClick={addRow} data-testid="rubric-add-row"><Plus className="mr-1.5 h-4 w-4" />Add criterion</Button>
          </>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={confirm} disabled={saving} className="bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="confirm-check-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Confirm &amp; continue
        </Button>
      </div>
    </div>
  );
}
